import { useState } from "react";
import { useListReturns, useCreateReturn, useListSales, useGetSale, getGetSaleQueryKey, getListReturnsQueryKey } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Separator } from "@/components/ui/separator";
import { Loader2, Plus, Package, Minus, ChevronsUpDown, Check } from "lucide-react";
import { format } from "date-fns";

function fmt(v: number) {
  return `₦${v.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

type SelectedItem = {
  saleItemId: number;
  productId: number;
  productName: string;
  quantity: number;       // how many to return
  maxQuantity: number;    // original quantity on the sale
  unitPrice: number;
  isService: boolean;
};

export default function Returns() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: returns, isLoading } = useListReturns();
  const { data: sales } = useListSales();
  const createReturn = useCreateReturn();

  const [isOpen, setIsOpen]         = useState(false);
  const [saleId, setSaleId]         = useState("");
  const [saleComboOpen, setSaleComboOpen] = useState(false);
  const [type, setType]             = useState("refund");
  const [reason, setReason]         = useState("");
  const [notes, setNotes]           = useState("");
  const [selected, setSelected]     = useState<Record<number, SelectedItem>>({});

  // Fetch the full sale (with items) whenever a sale is selected
  const { data: saleDetail, isLoading: loadingDetail } = useGetSale(
    Number(saleId),
    { query: { queryKey: getGetSaleQueryKey(Number(saleId)), enabled: !!saleId } }
  );

  // When a sale is picked, reset item selection
  const handleSaleChange = (id: string) => {
    setSaleId(id);
    setSelected({});
  };

  // Toggle item in/out of return basket
  const toggleItem = (item: any) => {
    const id = item.saleItemId;
    if (selected[id]) {
      const next = { ...selected };
      delete next[id];
      setSelected(next);
    } else {
      setSelected(prev => ({
        ...prev,
        [id]: {
          saleItemId: id,
          productId:   item.productId,
          productName: item.productName,
          quantity:    item.quantity,
          maxQuantity: item.quantity,
          unitPrice:   Number(item.unitPrice),
          isService:   false,
        },
      }));
    }
  };

  const setQty = (saleItemId: number, qty: number) => {
    setSelected(prev => {
      const item = prev[saleItemId];
      if (!item) return prev;
      const clamped = Math.max(1, Math.min(qty, item.maxQuantity));
      return { ...prev, [saleItemId]: { ...item, quantity: clamped } };
    });
  };

  const selectedItems  = Object.values(selected);
  const refundTotal    = selectedItems.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0);
  const canSubmit      = !!saleId && !!reason && selectedItems.length > 0;

  const reset = () => {
    setSaleId(""); setType("refund"); setReason(""); setNotes(""); setSelected({});
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    createReturn.mutate(
      {
        data: {
          saleId:  Number(saleId),
          type,
          amount:  refundTotal,
          reason,
          notes:   notes || undefined,
          returnItems: selectedItems.map(i => ({
            saleItemId: i.saleItemId,
            productId: i.productId,
            quantity:  i.quantity,
          })),
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListReturnsQueryKey() });
          setIsOpen(false);
          reset();
        },
        onError: (e: Error) => toast({ title: e.message || "Failed to process return", variant: "destructive" }),
      }
    );
  };

  const saleItems: any[] = (saleDetail as any)?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Returns</h1>
          <p className="text-muted-foreground mt-1">Process customer refunds and exchanges.</p>
        </div>

        <Dialog open={isOpen} onOpenChange={o => { setIsOpen(o); if (!o) reset(); }}>
          <DialogTrigger asChild>
            <Button><Plus className="w-4 h-4 mr-2" /> Process Return</Button>
          </DialogTrigger>

          <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden max-w-lg">
            <DialogHeader><DialogTitle>Process Return / Refund</DialogTitle></DialogHeader>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-1">
              {/* Step 1 — pick a sale */}
              <div>
                <label className="text-sm font-medium">Sale / Receipt</label>
                <Popover open={saleComboOpen} onOpenChange={setSaleComboOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={saleComboOpen}
                      className="w-full justify-between font-normal mt-1"
                    >
                      {saleId
                        ? (() => {
                            const s = (sales || []).find(s => String(s.id) === saleId);
                            return s ? `${s.receiptNumber} — ${fmt(Number(s.totalAmount))}` : "Select sale";
                          })()
                        : "Search by receipt number..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start" style={{ width: "var(--radix-popover-trigger-width)" }}>
                    <Command>
                      <CommandInput placeholder="Search receipt number..." />
                      <CommandList className="max-h-60">
                        <CommandEmpty>No matching sale found.</CommandEmpty>
                        <CommandGroup>
                          {(sales || []).map(s => (
                            <CommandItem
                              key={s.id}
                              value={`${s.receiptNumber} ${s.id}`}
                              onSelect={() => {
                                handleSaleChange(String(s.id));
                                setSaleComboOpen(false);
                              }}
                            >
                              <Check className={`mr-2 h-4 w-4 ${saleId === String(s.id) ? "opacity-100" : "opacity-0"}`} />
                              <span className="font-mono text-sm">{s.receiptNumber}</span>
                              <span className="ml-auto text-muted-foreground text-xs">{fmt(Number(s.totalAmount))}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Step 2 — pick items */}
              {saleId && (
                <div>
                  <p className="text-sm font-medium mb-2">
                    Select items to return
                  </p>

                  {loadingDetail ? (
                    <div className="flex justify-center py-6">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : saleItems.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No items found for this sale.
                    </p>
                  ) : (
                    <div className="border rounded-lg overflow-hidden divide-y">
                      {saleItems.map((item: any) => {
                        const isChecked = !!selected[item.saleItemId];
                        const sel       = selected[item.saleItemId];
                        return (
                          <div key={item.saleItemId} className={`flex items-center gap-3 px-3 py-2.5 transition-colors ${isChecked ? "bg-primary/5" : ""}`}>
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={() => toggleItem(item)}
                              id={`item-${item.saleItemId}`}
                            />
                            <label htmlFor={`item-${item.saleItemId}`} className="flex-1 min-w-0 cursor-pointer">
                              <p className="text-sm font-medium truncate">{item.productName}</p>
                              <p className="text-xs text-muted-foreground">
                                {fmt(Number(item.unitPrice))} × {item.quantity}
                              </p>
                            </label>

                            {isChecked && (
                              <div className="flex items-center gap-1 shrink-0">
                                <Button
                                  variant="outline" size="icon"
                                  className="h-6 w-6"
                                  onClick={() => setQty(item.saleItemId, (sel?.quantity ?? 1) - 1)}
                                  disabled={(sel?.quantity ?? 1) <= 1}
                                >
                                  <Minus className="w-3 h-3" />
                                </Button>
                                <span className="w-6 text-center text-sm font-medium">
                                  {sel?.quantity ?? 1}
                                </span>
                                <Button
                                  variant="outline" size="icon"
                                  className="h-6 w-6"
                                  onClick={() => setQty(item.saleItemId, (sel?.quantity ?? 1) + 1)}
                                  disabled={(sel?.quantity ?? 1) >= item.quantity}
                                >
                                  <Plus className="w-3 h-3" />
                                </Button>
                              </div>
                            )}

                            <span className="text-sm font-semibold w-20 text-right shrink-0">
                              {isChecked && sel
                                ? fmt(sel.quantity * sel.unitPrice)
                                : fmt(Number(item.unitPrice) * item.quantity)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {selectedItems.length > 0 && (
                    <div className="flex items-center justify-between mt-2 px-1">
                      <span className="text-xs text-muted-foreground">
                        {selectedItems.length} item{selectedItems.length > 1 ? "s" : ""} selected
                      </span>
                      <span className="text-sm font-bold text-destructive">{fmt(refundTotal)}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3 — type & reason */}
              {selectedItems.length > 0 && (
                <>
                  <Separator />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Return Type</label>
                      <Select value={type} onValueChange={setType}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="refund">Refund (cash back)</SelectItem>
                          <SelectItem value="exchange">Exchange (swap item)</SelectItem>
                          <SelectItem value="store_credit">Store credit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Return Value</label>
                      <div className="h-10 flex items-center border rounded-md px-3 bg-muted text-sm font-semibold">
                        {fmt(refundTotal)}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Estimate before original sale discount and VAT adjustments; the server calculates the final return amount from the sale record.</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium">Reason <span className="text-destructive">*</span></label>
                    <Input
                      value={reason}
                      onChange={e => setReason(e.target.value)}
                      placeholder="e.g. Defective product, Wrong size..."
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium">Notes (optional)</label>
                    <Input
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <p className="text-xs text-muted-foreground bg-muted rounded-md px-3 py-2">
                    {type === "refund"
                      ? "Returned items will be added back to stock. Cash refunds reduce net sales and loyalty points are deducted proportionally."
                      : type === "store_credit"
                        ? "Returned items will be added back to stock. Store credit is recorded as a sales return and remains a customer liability."
                        : "Returned items will be added back to stock. Record the replacement item(s) as a separate sale; this return value is not a cash refund."}
                  </p>

                  <Button
                    className="w-full"
                    onClick={handleSubmit}
                    disabled={createReturn.isPending || !canSubmit}
                  >
                    {createReturn.isPending
                      ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      : null}
                    Process {type === "refund" ? "Refund" : type === "store_credit" ? "Store Credit" : "Exchange"} · {fmt(refundTotal)}
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Returns history table */}
      <div className="border rounded-md overflow-hidden">
        <div className="overflow-y-auto max-h-[600px]">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>Receipt #</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : returns?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                    <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    No returns processed yet
                  </TableCell>
                </TableRow>
              ) : (
                [...(returns || [])].reverse().map(r => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-sm">{r.receiptNumber}</TableCell>
                    <TableCell>
                      <Badge variant={r.type === "refund" ? "destructive" : "secondary"}>
                        {r.type}
                      </Badge>
                    </TableCell>
                    <TableCell>{r.reason}</TableCell>
                    <TableCell><Badge variant="outline">{r.status}</Badge></TableCell>
                    <TableCell>{format(new Date(r.createdAt), "MMM d, yyyy")}</TableCell>
                      <TableCell className={`text-right font-medium ${r.type === "exchange" ? "" : "text-destructive"}`}>
                       {r.type === "exchange" ? fmt(Number(r.amount)) : `-${fmt(Number(r.amount))}`}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
